import { create } from 'zustand';
import type { RefreshEstimate } from '@/engine/Clock';
import type { InputProbeResult } from '@/engine/calibration';
import type { DeviceProfile } from '@/platform';

/**
 * 화면 전환은 라우터가 아니라 유한상태기계로 한다.
 * 세션 실행 중 브라우저 뒤로가기가 트라이얼을 파괴하는 것이 가장 큰 데이터 손실 위험이고,
 * 라우터를 쓰면 history 스택을 방어적으로 잠가야 한다. 화면이 몇 개 안 되므로 이쪽이 안전하다.
 */
export type Screen = 'boot' | 'home' | 'calibration' | 'session' | 'dashboard' | 'researcher' | 'tutorial' | 'interpretation' | 'stories' | 'roleplay' | 'records' | 'journey';

export interface CalibrationResult {
  measuredAt: string;
  refresh: RefreshEstimate;
  timerResolutionMs: number;
  crossOriginIsolated: boolean;
  input: InputProbeResult | null;
  pxPerMm: number | null;
  viewingDistanceMm: number;
  device: DeviceProfile | null;
}

export type Verdict = 'pass' | 'warn' | 'fail';

export type SessionStatus = 'idle' | 'preparing' | 'running' | 'scoring' | 'completed' | 'aborted';

export interface SessionState {
  status: SessionStatus;
  sessionId: string | null;
  participantCode: string;
  supervision: 'clinician' | 'self';
  lastScoreId: string | null;
  error: string | null;
  /** 튜토리얼에서 진입 — 결과 화면에 코치 설명을 붙인다 */
  tutorialMode: boolean;
  /** 선택된 프로토콜 `${id}@${version}` */
  protocolKey: string;
}

interface AppState {
  episodeId: string;
  selectEpisode: (episodeId: string) => void;
  screen: Screen;
  bridgeReady: boolean;
  calibration: CalibrationResult | null;
  session: SessionState;
  setSession: (patch: Partial<SessionState>) => void;

  go: (s: Screen) => void;
  setBridgeReady: (v: boolean) => void;
  setCalibration: (c: CalibrationResult | null) => void;
}

export const useApp = create<AppState>((set) => ({
  episodeId: 'cafe',
  selectEpisode: (episodeId) => set({ episodeId, screen: 'roleplay' }),
  screen: 'boot',
  bridgeReady: false,
  calibration: null,
  session: { status: 'idle', sessionId: null, participantCode: 'P000', supervision: 'clinician', lastScoreId: null, error: null, tutorialMode: false, protocolKey: 'dot-probe-anxiety-v1@1.0.0' },
  setSession: (patch) => set((s) => ({ session: { ...s.session, ...patch } })),

  go: (screen) => set({ screen }),
  setBridgeReady: (bridgeReady) => set({ bridgeReady }),
  setCalibration: (calibration) => set({ calibration }),
}));

/* ────────────────────────────────────────────────────────────
   품질 판정
   임계값의 근거는 노트 01 참조 — 재려는 편향 효과가 10–20 ms 이므로
   측정계의 지터가 그 근처에 오면 데이터가 의미를 잃는다.
   ──────────────────────────────────────────────────────────── */

export interface QualityCheck {
  id: string;
  label: string;
  value: string;
  verdict: Verdict;
  note: string;
}

export function evaluate(c: CalibrationResult): QualityCheck[] {
  const checks: QualityCheck[] = [];
  const { refresh } = c;

  checks.push({
    id: 'refresh',
    label: '디스플레이 주사율',
    value: `${refresh.hz} Hz · 프레임 ${refresh.frameMs.toFixed(2)} ms`,
    verdict: refresh.confident ? 'pass' : 'warn',
    note: refresh.confident
      ? '안정적으로 측정됨. 자극 지속시간을 프레임 단위로 환산한다.'
      : '측정이 불안정하다. 다른 탭·앱을 닫고 다시 측정하는 것을 권한다.',
  });

  const jitterRatio = refresh.sdDeltaMs / refresh.frameMs;
  checks.push({
    id: 'jitter',
    label: '프레임 간격 변산 (SD)',
    value: `${refresh.sdDeltaMs.toFixed(2)} ms · 프레임의 ${(jitterRatio * 100).toFixed(0)}%`,
    verdict: jitterRatio < 0.15 ? 'pass' : jitterRatio < 0.35 ? 'warn' : 'fail',
    note: '자극 지속시간의 흔들림으로 직결된다. 클수록 SOA 가 트라이얼마다 달라진다.',
  });

  const dropPct = refresh.sampleCount ? (refresh.droppedDuringProbe / refresh.sampleCount) * 100 : 0;
  checks.push({
    id: 'drop',
    label: '프레임 드롭률',
    value: `${dropPct.toFixed(1)} %`,
    verdict: dropPct < 1 ? 'pass' : dropPct < 3 ? 'warn' : 'fail',
    note: '드롭 한 번이 60 Hz 에서 16.7 ms 의 자극 연장이다. 재려는 효과와 같은 크기.',
  });

  checks.push({
    id: 'timer',
    label: 'performance.now() 눈금',
    value: c.timerResolutionMs < 0.001
      ? `${(c.timerResolutionMs * 1000).toFixed(1)} µs`
      : `${c.timerResolutionMs.toFixed(3)} ms`,
    verdict: c.timerResolutionMs <= 0.1 ? 'pass' : c.timerResolutionMs <= 1 ? 'warn' : 'fail',
    note: c.crossOriginIsolated
      ? '교차출처 격리 적용됨 — Spectre 완화로 인한 타이머 클램핑이 완화된 상태.'
      : '교차출처 격리가 적용되지 않았다. Firefox 는 이 상태에서 1 ms 로 뭉갠다.',
  });

  checks.push({
    id: 'coi',
    label: '교차출처 격리',
    value: c.crossOriginIsolated ? '적용됨' : '미적용',
    verdict: c.crossOriginIsolated ? 'pass' : 'warn',
    note: 'COOP/COEP 헤더가 필요하다. WebView 에서는 제어가 어려워 미적용이 정상일 수 있다.',
  });

  if (c.input) {
    checks.push({
      id: 'handler',
      label: '입력 핸들러 지연 (기계값)',
      value: `${c.input.meanHandlerDelayMs.toFixed(1)} ± ${c.input.sdHandlerDelayMs.toFixed(1)} ms`,
      verdict: c.input.sdHandlerDelayMs < 3 ? 'pass' : c.input.sdHandlerDelayMs < 8 ? 'warn' : 'fail',
      note: '이벤트 생성 → 핸들러 실행. event.timeStamp 를 쓰면 이 값이 RT 에서 빠진다.',
    });
    checks.push({
      id: 'rtsd',
      label: '단순반응시간 변산 (기기+참가자)',
      value: `중앙값 ${c.input.medianRtMs.toFixed(0)} ms · SD ${c.input.sdRtMs.toFixed(0)} ms`,
      verdict: c.input.sdRtMs < 60 ? 'pass' : c.input.sdRtMs < 110 ? 'warn' : 'fail',
      note: '이 SD 가 편향 점수 표준오차의 하한을 정한다. 사람의 변산이 대부분을 차지한다.',
    });
  }

  checks.push({
    id: 'physical',
    label: '화면 물리 크기',
    value: c.pxPerMm ? `${c.pxPerMm.toFixed(2)} px/mm` : '미측정',
    verdict: c.pxPerMm ? 'pass' : 'warn',
    note: '자극의 시각도를 세션 간 일정하게 유지하려면 필요하다. 없으면 논리 단위로만 배치한다.',
  });

  return checks;
}

export function overallVerdict(checks: QualityCheck[]): Verdict {
  if (checks.some((c) => c.verdict === 'fail')) return 'fail';
  if (checks.some((c) => c.verdict === 'warn')) return 'warn';
  return 'pass';
}
